import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Check } from "lucide-react";
import { Link } from "wouter";
import { useIsV3Billing } from "@/hooks/use-v3-billing";
import {
  MONTHLY_PRICES_PER_ROOM,
  ANNUAL_PRICES_PER_ROOM_PER_MONTH,
  isFlatPricedTier,
  flatPlanMonthlyPriceUsd,
  planDisplayName,
  HOST_MAX_ROOMS_PER_PROPERTY,
  HOST_MAX_ACTIVE_PROPERTIES,
  type AccountType,
} from "@shared/schema";

interface UpgradeContentProps {
  feature: string;
  requiredTier: AccountType;
  reason: string;
  /**
   * Optional marketing feature page (e.g. `/features/operations/tasks`). When
   * provided, a "Want to learn more" link is shown so the user can read about
   * the gated feature before upgrading.
   */
  learnMoreHref?: string;
  /**
   * When provided, a dismiss button is rendered (e.g. "Maybe later" in the
   * modal, "Back to dashboard" in the route overlay). Omit it for a gate that
   * has no dismiss affordance.
   */
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * Fired when the upgrade CTA or the learn-more link is clicked, in addition
   * to the navigation the link itself performs. The modal uses this to close
   * itself; the route overlay can leave it undefined.
   */
  onNavigate?: () => void;
}

/**
 * Presentational upgrade card shared by `UpgradeModal` (wrapped in a Radix
 * Dialog) and the route-level tier-lock overlay in `app-layout`. Uses plain
 * markup with no Dialog-specific structure so it can render outside a Dialog.
 */
export function UpgradeContent({
  feature,
  requiredTier,
  reason,
  learnMoreHref,
  onDismiss,
  dismissLabel = "Maybe later",
  onNavigate,
}: UpgradeContentProps) {
  const monthlyPrice = MONTHLY_PRICES_PER_ROOM[requiredTier];
  const annualPrice = ANNUAL_PRICES_PER_ROOM_PER_MONTH[requiredTier];
  // Flat-priced entry tiers (Research/Host) bill a single monthly price, not
  // rooms × rate, so they get their own branch with no per-room math.
  const isFlat = isFlatPricedTier(requiredTier);
  const flatPrice = flatPlanMonthlyPriceUsd(requiredTier);
  // What the flat-priced plan actually includes, spelled out so the host knows
  // exactly what the flat monthly price covers (Host caps at 1 property /
  // HOST_MAX_ROOMS_PER_PROPERTY rooms; Research is the search-only entry tier).
  const flatInclusions: string[] = !isFlat
    ? []
    : requiredTier === "Host"
      ? [
          `Up to ${HOST_MAX_ACTIVE_PROPERTIES} ${HOST_MAX_ACTIVE_PROPERTIES === 1 ? "property" : "properties"}`,
          `Up to ${HOST_MAX_ROOMS_PER_PROPERTY} rooms`,
          "Unlimited searches",
        ]
      : ["Unlimited Buy Box searches"];
  const isFree = !isFlat && (monthlyPrice === 0 || monthlyPrice === null);
  const hasAnnualDiscount =
    !isFlat && !isFree && annualPrice !== null && monthlyPrice !== null && annualPrice < monthlyPrice;

  return (
    <div>
      <div className="flex flex-col items-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-2">
          <Lock className="h-6 w-6 text-amber-500" />
        </div>
        <h2 className="text-center text-lg font-semibold leading-none tracking-tight">
          Upgrade to {planDisplayName(requiredTier)}
        </h2>
        <p className="text-center text-sm text-gray-500 mt-1.5">{reason}</p>
      </div>
      <div className="space-y-4 pt-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm font-medium text-gray-700">{feature}</p>
          {isFlat && flatPrice !== null ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="text-upgrade-price">
                ${flatPrice}<span className="text-sm font-normal text-gray-500">/mo</span>
              </p>
              <p className="text-xs text-gray-500 mt-1" data-testid="text-upgrade-flat-note">
                Flat monthly price — no per-room billing
              </p>
              {flatInclusions.length > 0 && (
                <ul className="mt-3 inline-block text-left space-y-1" data-testid="list-upgrade-includes">
                  {flatInclusions.map((item, i) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 text-sm text-gray-700"
                      data-testid={`text-upgrade-include-${i}`}
                    >
                      <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : isFree ? (
            <p className="text-2xl font-bold text-gray-900 mt-1">Free</p>
          ) : hasAnnualDiscount ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="text-upgrade-price">
                ${annualPrice}
                <span className="text-sm font-normal text-gray-500">/room/mo*</span>
              </p>
              <p className="text-xs text-gray-500 mt-1" data-testid="text-upgrade-price-footnote">
                <span className="line-through">${monthlyPrice}/room/mo</span>{" "}
                <span className="text-emerald-600 font-semibold">Save ~17%</span>
              </p>
              <p className="text-[11px] text-gray-500 mt-2" data-testid="text-upgrade-price-disclaimer">
                *Price shown with the annual discount, billed yearly. Monthly billing available at ${monthlyPrice}/room/mo.
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${monthlyPrice}<span className="text-sm font-normal text-gray-500">/room/mo</span>
              </p>
            </>
          )}
        </div>
        <div className="flex gap-3">
          {onDismiss && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={onDismiss}
              data-testid="button-upgrade-dismiss"
            >
              {dismissLabel}
            </Button>
          )}
          <Link href="/app/account/billing" className="flex-1">
            <Button
              className="w-full bg-brand-accent hover:bg-[#e69500] text-white"
              onClick={onNavigate}
              data-testid="button-upgrade-cta"
            >
              Upgrade to {planDisplayName(requiredTier)} <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>
        {learnMoreHref && (
          <p className="text-center text-sm">
            <Link
              href={learnMoreHref}
              className="text-brand hover:underline font-medium"
              onClick={onNavigate}
              data-testid="link-upgrade-learn-more"
            >
              Want to learn more?
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: string;
  requiredTier: AccountType;
  reason: string;
  learnMoreHref?: string;
}

export function UpgradeModal({ open, onOpenChange, feature, requiredTier, reason, learnMoreHref }: UpgradeModalProps) {
  // v3 per-property accounts have every feature included, so the legacy tier
  // upgrade pitch never opens for them (B13).
  const isV3 = useIsV3Billing();
  return (
    <Dialog open={open && !isV3} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        {/* sr-only title/description satisfy Radix Dialog a11y; the visible
            heading + reason are rendered inside UpgradeContent. */}
        <DialogTitle className="sr-only">Upgrade to {planDisplayName(requiredTier)}</DialogTitle>
        <DialogDescription className="sr-only">{reason}</DialogDescription>
        <UpgradeContent
          feature={feature}
          requiredTier={requiredTier}
          reason={reason}
          learnMoreHref={learnMoreHref}
          onDismiss={() => onOpenChange(false)}
          dismissLabel="Maybe later"
          onNavigate={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
